import {
  OpenedFeedback,
  SubmittedFeedback,
  type CompleteScreenshot,
  type OpenFeedback,
  type SubmitFeedback,
} from '@myrewards/contracts';
import { z } from 'zod';
import { request } from '../api/client';

// APP-104 §5, §9: the browser's side of sending feedback. The feedback calls are left out of the
// request ring (diagnostics.ts), so a report never lists its own sending.

const root = '/api/v1/feedback';
const one = (id: string): string => `${root}/${encodeURIComponent(id)}`;

/** Opens (or re-opens: the id is the browser's) the draft, declaring the screenshot to come. */
export function openFeedback(body: OpenFeedback) {
  return request(root, { method: 'POST', body, schema: OpenedFeedback });
}

/** Sends the picture as one multipart `file` part. */
export function uploadScreenshot(id: string, bytes: Uint8Array, signal: AbortSignal) {
  const form = new FormData();
  form.append(
    'file',
    new Blob([bytes as Uint8Array<ArrayBuffer>], { type: 'image/png' }),
    'screenshot.png',
  );
  return request(`${one(id)}/screenshot`, {
    method: 'PUT',
    body: form,
    schema: z.undefined(),
    signal,
  });
}

/** Says the upload ended without the file, so the report settles as "omitted" at once. */
export function completeScreenshot(id: string, body: CompleteScreenshot) {
  return request(`${one(id)}/screenshot/complete`, {
    method: 'POST',
    body,
    schema: z.object({ screenshot: z.string() }),
  });
}

export function submitFeedback(id: string, body: SubmitFeedback) {
  return request(`${one(id)}/submit`, { method: 'POST', body, schema: SubmittedFeedback });
}

const VoiceNow = z.object({
  features: z
    .object({
      feedback: z.object({
        voice: z.boolean(),
        voiceSecondsLeft: z.number().int().nonnegative().optional(),
      }),
    })
    .optional(),
});

/**
 * Has today's voice time run out? Asked only when an open gave no voice ticket, to tell "used up"
 * (a calm line) from "voice isn't offered now" (`/v1/me`, APP-104 §7).
 */
export async function voiceUsedUp(): Promise<boolean> {
  const me = await request('/api/v1/me', { schema: VoiceNow });
  const feedback = me.features?.feedback;
  return feedback?.voice === true && feedback.voiceSecondsLeft === 0;
}

/** Throws a draft away. */
export function discardFeedback(id: string) {
  return request(one(id), { method: 'DELETE', schema: z.undefined() });
}
